import React from "react";
import { StyleSheet, View, Dimensions, Platform } from "react-native";
import { cloneDeep, isEqual, find } from "lodash";
import moment from "moment";

import {
  durationToHHmm,
  hasItems,
  getActivityModel,
  getTagModel,
  getStorageKey,
  getStorageItem,
  setStorageItem,
  navigate,
} from "../../../helpers/utils";

import { ReflectionProvider } from "../reflection/ReflectionContext";
import AppMessages from "../../../config/AppMessages";
import appStyles from "../../appStyles";
import BottomButton from "../common/BottomButton";
import OnboardingConfirmDialog from "./OnboardingConfirmDialog";
import LastWeekElements from "./last-week/LastWeekElements";
import LastWeekFabButton from "./last-week/LastWeekFabButton";
import LastWeekHeader from "./last-week/LastWeekHeader";
import LastWeekHelper from "./last-week/LastWeekHelper";
import LastWeekTemplateHours from "./last-week/LastWeekTemplateHours";
import LastWeekTimePicker from "./last-week/LastWeekTimePicker";
import TutorialsHelper from "../tutorials/TutorialsHelper";

const { height: HEIGHT } = Dimensions.get("window");

const SyncInProgressMessage = AppMessages.SyncInProgress;

import TUTORIALS_DATA from "../tutorials/data";
import AppTheme from "../AppTheme";
import JournalDialog from "../common/JournalDialog";

export default class LastWeekPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.templateName = props.templateName;
    this.storageKey = getStorageKey(this.controller, "TEMPLATE_ACTIVITIES");

    this.matchCard =
      this.templateName === "AutoTrack"
        ? (this.controller.state?.cards_v2 || []).filter(
            (c) => c.type === "autoreview",
          )
        : null;

    this.WEEKLY_HOURS = LastWeekHelper.WEEKLY_HOURS;

    this.state = {
      message: SyncInProgressMessage,
      showConfirm: false,
      uncategorizedActivity: this.getUncategorizedActivity(),
      openConfirmation: false,
      showJournalDialog: "",
      template_data: [],
      hideActions: false,
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.refreshPage();
        },
      );
    } else {
      this.refreshPage();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  refreshPage = async () => {
    const { activity, loadFromCache } = this.getArgs() || {};

    // Tutorials
    this.initTutorials();

    // If new element was added when coming from edit page
    if (activity && activity.id_activity) {
      if (
        activity.children != null &&
        activity.children.length > 0 &&
        !activity.id.endsWith("_misc")
      ) {
        activity.parents = [activity.id];
        activity.id = activity.id + "_misc";
        activity.name += " (Generic)";
        activity.children = null;
      }
      this.initTemplateModel({
        templateActivities: [...this.__getOldData(), activity],
        shouldRefresh: true,
        showElements: true,
        showAllActivities: true,
      });
      // Edit tag/activity
    } else if (loadFromCache) {
      getStorageItem(this.storageKey).then((cachedData) => {
        if (cachedData) {
          this.initTemplateModel({
            templateActivities: cachedData,
            shouldRefresh: true,
          });
        } else {
          this.loadData();
        }
      });
    } else {
      this.loadData();
    }
  };

  __getOldData = () => {
    return this.controller.state?.template_data;
  };

  loadData = () => {
    const oldData = this.__getOldData();
    // Check if uncategorized needs to be added
    if (oldData) {
      this.initActivitiesList({
        templateActivities: oldData,
        updateOld: true,
        autosave: false,
      });
    }

    this.fetchApiData();
  };

  fetchApiData = () => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
      message: SyncInProgressMessage,
    }));

    const entity = "template_data";
    const params = { templateName: this.templateName };
    this.controller.getApiData(entity, params, (templateActivities) => {
      const totalHours = templateActivities.reduce(
        (acc, a) => acc + 1 * (a.total_hours || 0),
        0,
      );
      const newHours = this.templateName === "AutoTrack" ? totalHours : 168;

      if (
        isEqual(this.state.activities_old, templateActivities) &&
        this.WEEKLY_HOURS == newHours
      ) {
        this.setState((state) => ({
          ...state,
          isLoading: false,
          message: "",
        }));

        return;
      }

      this.WEEKLY_HOURS = newHours;

      // Check if uncategorized needs to be added
      this.initActivitiesList({
        templateActivities,
        updateOld: true,
        autosave: false,
      });

      // By default fit the average template
      if (this.templateName === "average") {
        this.fitHours();
      }
    });
  };

  // ------------------------------------- MVP -------------------------------------
  initActivitiesList = ({
    templateActivities,
    updateOld = false,
    autosave = true,
  }) => {
    const { uncategorizedActivity } = this.state;

    templateActivities = templateActivities.filter(
      (ta) => ta.id_activity != uncategorizedActivity.id_activity,
    );

    const totalHours = templateActivities.reduce(
      (acc, a) => acc + 1 * (a.total_hours || 0),
      0,
    );

    // If hours number < weekly hours add Uncategorized
    if (totalHours < this.WEEKLY_HOURS) {
      const hoursDiff = this.WEEKLY_HOURS - totalHours;
      templateActivities = [
        ...templateActivities,
        {
          ...uncategorizedActivity,
          total_hours: hoursDiff,
          hours:
            hoursDiff > 0 ? hoursDiff / LastWeekHelper.getDailyAdjustment() : 0,
        },
      ];
    }

    this.initTemplateModel({
      templateActivities,
      shouldRefresh: true,
      updateOld,
      autosave,
    });
  };

  initTemplateModel = (params) => {
    const {
      templateActivities,
      shouldRefresh = false,
      showElements = false,
      updateOld = false,
      showAllActivities = false,
      autosave = true,
    } = params;

    this.setState((state) => ({ ...state, isLoading: false }));

    const templateModel = LastWeekHelper.getTemplateDataModel(
      templateActivities,
      this.controller.state?.elements,
      this.controller.state?.reflection_root,
      true,
    );

    this.setState(
      (state) => ({
        ...state,
        isLoading: false,
        template_data: templateModel,
        activities: [...templateActivities],
        activities_old: updateOld
          ? cloneDeep(templateActivities)
          : this.state.activities_old,
        showElements,
        showAllActivities,
      }),
      () => {
        if (shouldRefresh) this.updateMariMessage();

        const { showTutorials, tutorialStep } = this.state;

        // Autosave
        if (showTutorials) {
          if (tutorialStep >= 10) {
            this.loadTutorial(11);
          }
        } else if (autosave) {
          this.autoSaveHours();
        }
      },
    );
  };

  // ------------------------------------- MESSAGES -------------------------------------
  updateMariMessage = () => {
    const totalHours = this.getTotalWeekHours();

    const message =
      totalHours < this.WEEKLY_HOURS
        ? AppMessages.NotEnoughHours.replace("{TOTAL}", totalHours).replace(
            "{AVAILABLE}",
            this.WEEKLY_HOURS,
          )
        : totalHours > this.WEEKLY_HOURS
        ? AppMessages.TooManyHours.replace("{TOTAL}", totalHours).replace(
            "{AVAILABLE}",
            this.WEEKLY_HOURS,
          )
        : "";

    this.setState((state) => ({
      ...state,
      message,
    }));

    return message;
  };

  showTempMessage = (message, duration = 3000) => {
    this.setState((state) => ({ ...state, message }));

    setTimeout(() => {
      this.setState((state) => ({ ...state, message: "" }));
    }, duration);
  };

  // ------------------------------------- TIMEPICKER -------------------------------------
  showTimePicker = (selectedItem, day = {}, dayIndex = 0, level = 0) => {
    if (selectedItem.item_type == "set") return;

    if (selectedItem.is_uncategorized) {
      this.showTempMessage(AppMessages.EditUncategorized);
      return;
    }

    const { hours, minutes } = day.hours
      ? durationToHHmm(day.hours)
      : { hours: 0, minutes: 0 };

    this.setState((state) => ({
      ...state,
      selectedItem: {
        ...selectedItem,
        dayIndex,
        level,
        time: `${hours}:${minutes}`,
      },
    }));
  };

  closeTimePicker = () => {
    this.setState((state) => ({
      ...state,
      selectedItem: null,
    }));
  };

  updateEndTime = (selectedDecTime) => {
    const { selectedItem, showTutorials, tutorialStep } = this.state;

    if (showTutorials && tutorialStep < 11) {
      this.loadTutorial(tutorialStep + 1);
    }

    if (selectedDecTime == selectedItem.time) return;

    const newActivities = this.state.activities;
    newActivities.forEach((act) => {
      if (selectedItem.item_id == act.id_activity) {
        act.is_modified = true;
        act.dayIndex = selectedItem.dayIndex;
        if (!LastWeekHelper.isTotalHours(selectedItem)) {
          act.hours = selectedDecTime;
          act.total_hours = act.hours * 7;
        } else {
          act.total_hours = selectedDecTime;
          act.hours =
            selectedDecTime > 0
              ? selectedDecTime / LastWeekHelper.getDailyAdjustment()
              : 0;
        }
      }
    });

    this.initActivitiesList({
      templateActivities: newActivities,
      updateOld: false,
      autosave: !showTutorials,
    });
  };

  getTotalWeekHours = () => {
    const { template_data } = this.state;

    let total = 0;
    let counted = {};
    (template_data || []).forEach((group) => {
      total += this.sumLeaves(group, counted);
    });

    const timeInterval =
      this.controller.state.settings?.interval_duration / 60.0;
    return timeInterval * Math.round(total / timeInterval);
  };

  sumLeaves(item, counted) {
    var total = 0;
    const hasChildren = item.children != null && item.children.length > 0;
    if (!hasChildren) {
      if (!counted[item.item_id]) {
        counted[item.item_id] = 1;
        total = 1 * (hasItems(item.days) ? item.days[1]?.hours : 0);
      }
    } else {
      (item.children || []).forEach((child) => {
        total += this.sumLeaves(child, counted);
      });
    }
    return total;
  }

  // ------------------------------------- CONFIRM -------------------------------------
  autoSaveHours = (callback = undefined) => {
    const { template_data } = this.state;

    const data = LastWeekHelper.mapElementsToTemplateHours(template_data);

    this.setState((state) => ({ ...state, isLoading: true }));
    this.controller.tracking.track(this.templateName);

    // Average week
    this.controller.cardActionWithCallback(
      {
        action: "save",
        target: "template",
        template_name: this.templateName,
        data: data,
      },
      () => {
        this.setState((state) => ({ ...state, isLoading: false }));
        if (typeof callback == "function") callback();
      },
    );
  };

  // ------------------------------------- FAB -------------------------------------
  fitHours = (fitToUncategorized = false) => {
    const { uncategorizedActivity, activities } = this.state;

    const timeInterval =
      this.controller.state.settings?.interval_duration / 60.0;

    LastWeekHelper.fitHours({
      uncategorizedActivity,
      activities,
      timeInterval,
      fitToUncategorized,
      callback: this.initTemplateModel,
    });
  };

  getUncategorizedActivity = () => {
    const pattern = new RegExp("[Uu]ncategorized".split(" ").join("|"), "gi");

    // Find uncategorized
    const uncategorizedActivity = find(this.controller.state?.elements, (a) => {
      return a.name && a.name.match(pattern);
    });

    return uncategorizedActivity && uncategorizedActivity.id
      ? {
          is_uncategorized: true,
          id_activity: uncategorizedActivity.id,
          total_hours: 0,
          hours: 0,
        }
      : {};
  };

  onDeleteElement = (element) => {
    const { activities, tutorialStep } = this.state;

    const remainingActivities = activities.filter(
      (a) => a.id_activity != element.item_id,
    );

    this.initActivitiesList({
      templateActivities: remainingActivities,
      updateOld: false,
      autosave: true,
    });

    if (tutorialStep == 5) this.loadTutorial(tutorialStep + 1);
  };

  goToElementsPage = () => {
    this.setState((state) => ({ ...state, showElements: true }));
  };

  goBack = (elements) => {
    const { showElements, showTutorials, tutorialStep } = this.state;

    if (showTutorials) {
      if (showElements) {
        this.loadTutorial(tutorialStep + 2);
      } else {
        return this.goBackOneStep();
      }
    }

    // Already on Onboard page => go to feed
    if (!showElements) {
      navigate(this.controller, "rn:home_card");
    }

    // Coming from elements page
    if (!elements) {
      this.setState((state) => ({ ...state, showElements: false }));
      return;
    }

    // Elements confirmed
    if (elements.activities) {
      this.setState((state) => ({ ...state, showElements: false }));
      const newElements = [];
      elements.activities.forEach((a) => {
        const act = JSON.parse(JSON.stringify(a));
        if (act.children != null && act.children.length > 0) {
          act.parents = [act.id];
          act.id += "_misc";
          act.id_activity += "_misc";
          act.name += " (Generic)";
          act.children = null;
        }
        newElements.push(act);
      });

      this.initActivitiesList({
        templateActivities: newElements,
        shouldRefresh: true,
        autosave: true,
      });

      /*  this.initTemplateModel({
        templateActivities: elements.activities,
        shouldRefresh: true,
        autosave: true,
      }); */
    }
  };

  continueAction = (elements) => {
    if (!this.state.showElements && this.templateName === "AutoTrack") {
      const action = {
        action: "autotrack",
        fillAverage: false,
      };
      console.log("Continue Autotrack");
      this.controller.cardActionWithCallback(action, () =>
        navigate(this.controller, "rn:home_card"),
      );
    } else {
      this.goBack(elements);
    }
  };

  // ------------------------------------- UNDO -------------------------------------
  undoChanges = () => {
    this.setState((state) => ({ ...state, openConfirmation: true }));
  };

  closeConfirmation = () => {
    this.setState((state) => ({ ...state, openConfirmation: false }));
  };

  confirmUndoChanges = () => {
    this.setState((state) => ({ ...state, openConfirmation: false }));
    this.initTemplateModel({
      templateActivities: this.state.activities_old || [],
      shouldRefresh: true,
    });
  };

  goToEditPage = (element) => {
    if (element.is_uncategorized) return;

    setStorageItem(this.storageKey, this.state.activities);

    const selectedPage =
      element.item_type == "activity" ? "rn:edit_activity" : "rn:edit_tag";

    const model =
      element.item_type == "activity"
        ? {
            activity: getActivityModel(element),
            loadFromCache: true,
          }
        : {
            tag: getTagModel(element),
            loadFromCache: true,
          };

    this.controller.changePage("feed", {
      selectedPage: selectedPage,
      args: model,
    });
  };

  // ------------------------------------- TUTORIALS -------------------------------------
  initTutorials = () => {
    const showTutorials = (this.controller?.state?.showTutorials || {})[
      TutorialsHelper.STORAGE_KEYS.reflection
    ];

    if (showTutorials) {
      this.loadTutorial(1);
    } else {
      this.setState((state) => ({
        ...state,
        mariMessage: "",
        tutorialStep: 0,
        showTutorials: false,
      }));
    }
  };

  loadTutorial = (step = 1) => {
    const tutorial =
      step >= 0 ? TUTORIALS_DATA["average_week"][step - 1] || {} : {};

    if (step == 11) {
      const message = this.updateMariMessage();
      if (message) {
        this.setState((state) => ({
          ...state,
          tutorialMessage: message,
          hideActions: false,
          mariAction: {},
        }));
        return;
      }
    }

    this.setState((state) => ({
      ...state,
      showTutorials: true,
      tutorialStep: step <= 11 ? step : 11,
      hideActions: step != 6 && step != 11,
      tutorialMessage: tutorial.message,
      mariAction: tutorial.action
        ? {
            text: tutorial.action.text,
            onPress: () => {
              if (step == 11) {
                this.confirmEstimates();
              } else {
                this.loadTutorial(step + 1);
              }
            },
          }
        : {},
    }));
  };

  confirmEstimates = () => {
    this.autoSaveHours(() => {
      this.goToReflectionEditPage();
    });
  };

  goBackOneStep = () => {
    const { tutorialStep } = this.state;
    if (tutorialStep > 1) {
      this.loadTutorial(tutorialStep - 1);
    } else {
      this.controller.toggleTutorialsMode();
      navigate(this.controller, "rn:home_card");
    }
  };

  toggleJournalDialog = (isOpen) => {
    const { showTutorials, tutorialStep } = this.state;

    if (showTutorials && tutorialStep != 8 && tutorialStep != 9) return;

    this.setState(
      (state) => ({
        ...state,
        showJournalDialog: isOpen,
      }),
      () => {
        if (showTutorials) {
          this.loadTutorial(tutorialStep + 1);
        }
      },
    );
  };

  getJournalModel = () => {
    return {
      item_id: "",
      time_unit: "week",
      day: TutorialsHelper.getLastWeekDate(),
      item_type: "",
      hours: 0,
    };
  };

  goToReflectionEditPage = () => {
    this.controller.changePage("rn:sets_reflection_detail", {
      args: {
        reflection: {
          edit_time_unit: "week",
          edit_object: "time",
          edit_day: TutorialsHelper.getLastWeekDate(),
        },
        fetchFromApi: true,
      },
    });
  };

  render() {
    const {
      template_data,
      selectedItem,
      showElements,
      isLoading,
      message,
      activities,
      openConfirmation,
      mariAction,
      showTutorials,
      tutorialStep,
      showJournalDialog,
      tutorialMessage,
    } = this.state;

    const minutesInterval =
      this.controller.state?.settings?.interval_duration || 1;

    const autoTrackData =
      this.matchCard && this.matchCard.length > 0 && this.matchCard[0];
    const from = autoTrackData
      ? moment(autoTrackData.from).format("MMM D")
      : null;
    const to = autoTrackData ? moment(autoTrackData.to).format("MMM D") : null;

    const isShortInterval =
      autoTrackData &&
      moment(autoTrackData.to).diff(moment(autoTrackData.from), "days") < 7;

    let title = autoTrackData
      ? from === to
        ? from
        : from + " - " + to
      : "Last Week";
    let subtitle =
      "Fill in your best estimates for " +
      (isShortInterval ? "this interval" : "last week");

    if (showTutorials) {
      title = "Last week";
      subtitle = "Fill in your best estimates";
    }

    return (
      <ReflectionProvider
        value={{
          controller: this.controller,
          pageType: "onboarding",
          totalCount: template_data?.length,
          onDeleteElement: this.onDeleteElement,
          onHoursSelected: this.showTimePicker,
          onElementSelected: this.goToEditPage,
          goBackOneStep: this.goBackOneStep,
          showTutorials,
          tutorialStep,
        }}
      >
        <View style={appStyles.container}>
          {showElements ? (
            <LastWeekElements
              controller={this.controller}
              currentActivities={activities}
              goBack={this.goBack}
              showAllActivities={this.state.showAllActivities}
            />
          ) : (
            <>
              <LastWeekHeader
                title={title}
                subTitle={subtitle}
                isLoading={isLoading}
                goBack={this.goBack}
                onJournalIconPress={this.toggleJournalDialog}
              />

              {hasItems(template_data) ? (
                <LastWeekTemplateHours
                  templateData={template_data}
                  controller={this.controller}
                />
              ) : null}

              <LastWeekFabButton
                style={{ bottom: showTutorials ? 0 : 70 }}
                fitHours={this.fitHours}
                goToElementsPage={this.goToElementsPage}
                undoChanges={this.undoChanges}
                weeklyHours={this.WEEKLY_HOURS}
                mariOptions={{
                  mariMessage:
                    selectedItem || showJournalDialog
                      ? ""
                      : tutorialMessage || message,
                  mariAction:
                    selectedItem || showJournalDialog ? {} : mariAction,
                  isSelected: tutorialStep == 6,
                  tutorialStep,
                  showTutorials,
                }}
              />

              <View style={styles.footerOptions}>
                {!showTutorials ? (
                  <BottomButton
                    styles={styles}
                    buttonText="Continue"
                    onPress={this.continueAction}
                  />
                ) : null}
              </View>
            </>
          )}

          {/* Journal popup */}
          {showJournalDialog ? (
            <JournalDialog
              type={"reflection"}
              item={this.getJournalModel()}
              controller={this.controller}
              close={() => this.toggleJournalDialog(false)}
            />
          ) : null}

          {/* TimePicker popup */}
          <LastWeekTimePicker
            selectedItem={selectedItem}
            onClose={this.closeTimePicker}
            onChange={this.updateEndTime}
            minutesInterval={minutesInterval}
          />

          {/* Confirm Undo Dialog */}
          <OnboardingConfirmDialog
            isOpen={openConfirmation}
            title="Are you sure?"
            description="Tapping confirm will revert all manual changes to hour counts to default values."
            onConfirm={this.confirmUndoChanges}
            onClose={this.closeConfirmation}
          />
        </View>
      </ReflectionProvider>
    );
  }
}

const styles = StyleSheet.create({
  scrollView: {
    maxHeight: HEIGHT - 130,
  },
  footerOptions: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
  },
  bottomButton: {
    borderRadius: 25,
    backgroundColor: AppTheme.DEFAULT_COLOR,
    shadowOpacity: 0.25,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowColor: "#000",
    shadowRadius: 4,
    elevation: 5,
  },
  bottomButtonText: {
    fontFamily:  Platform.select({ default: "Montserrat", android: "Montserrat-SemiBold" }),
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 1,
    color: "#FFF",
    textAlign: "center",
  },
});
